import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Circle } from "lucide-react";
import { toast } from "sonner";
import { ProductInformation } from "@/components/ProductInformation";
import { TraceabilityPanel } from "@/components/TraceabilityPanel";
import { Reveal } from "@/components/ui/reveal";
import TypedHandStyle from "@/components/ui/TypedHandStyle";

const CTA_HEADLINE = "Every Roskam product is made of premium materials and craftsmanship.";

export default function HomePage() {
  const featuredProduct = {
    name: "Roskam Wool Knit",
    heroImage: "/roskam_farm_one.jpg",
    productImages: [
      "/00_productimage_roskam.jpg",
      "/01_productimage_roskam.jpg",
      "/02_productimage_roskam.jpg",
      "/03_productimage_roskam.jpg",
      "/04_productimage_roskam.jpg"
    ]
  };
  const productSizing = {
    fit: "Relaxed, everyday silhouette.",
    guide: [
      { size: "One size", length: "230 cm", width: "60 cm" }
    ]
  };
  const productCare = {
    summary: "Air out between wears. Hand wash cold or dry clean. Dry flat and reshape gently.",
    instructions: [
      "Hand wash cold with wool detergent.",
      "Do not tumble dry; dry flat.",
      "Store folded to maintain shape."
    ]
  };
  const traceability = {
    lastUpdated: "2026-02-05",
    steps: [
      {
        name: "Wool sourcing & spinning",
        partner: "Sued Wolle Group",
        location: "EU supply chain",
        date: "2026"
      },
      {
        name: "Knitting & finishing",
        partner: "Knitwear Lab",
        location: "NL",
        date: "2026"
      },
      {
        name: "You",
        partner: "Customer",
        location: "Delivered to your door",
        date: "2026"
      }
    ]
  };
  const waitlistEndpoint = process.env.REACT_APP_FORMSPREE_WAITLIST_ENDPOINT || "";
  const contactEndpoint = process.env.REACT_APP_FORMSPREE_CONTACT_ENDPOINT || "";

  const [waitlistForm, setWaitlistForm] = useState({
    name: "",
    email: ""
  });
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isWaitlistSubmitting, setIsWaitlistSubmitting] = useState(false);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [startCtaTyping, setStartCtaTyping] = useState(false);
  const [selectedProductImage, setSelectedProductImage] = useState(0);
  const [productCarouselApi, setProductCarouselApi] = useState(null);
  const ctaSectionRef = useRef(null);

  useEffect(() => {
    const section = ctaSectionRef.current;
    if (!section || startCtaTyping) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStartCtaTyping(true);
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [startCtaTyping]);

  useEffect(() => {
    if (!productCarouselApi) return;

    const syncSelectedImage = () => {
      setSelectedProductImage(productCarouselApi.selectedScrollSnap());
    };

    syncSelectedImage();
    productCarouselApi.on("select", syncSelectedImage);
    productCarouselApi.on("reInit", syncSelectedImage);

    return () => {
      productCarouselApi.off("select", syncSelectedImage);
      productCarouselApi.off("reInit", syncSelectedImage);
    };
  }, [productCarouselApi]);

  const postFormspree = async (endpoint, payload) => {
    if (!endpoint) {
      throw new Error("Formspree endpoint not configured.");
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Request failed");
    }

    return response.json();
  };

  const handleWaitlistSubmit = async (event) => {
    event.preventDefault();
    setIsWaitlistSubmitting(true);

    try {
      await postFormspree(waitlistEndpoint, {
        name: waitlistForm.name,
        email: waitlistForm.email
      });
      toast.success("You're on the waitlist.");
      setWaitlistForm({ name: "", email: "" });
    } catch (error) {
      toast.error("Unable to submit the waitlist form.");
    } finally {
      setIsWaitlistSubmitting(false);
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setIsContactSubmitting(true);

    try {
      await postFormspree(contactEndpoint, {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message
      });
      toast.success("Message sent. We'll be in touch.");
      setContactForm({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Unable to send your message.");
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero / Pilot Product */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={featuredProduct.heroImage}
            alt="Roskam farm fields"
            className="w-full h-full object-cover object-[center_50%]"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/70 to-background/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal delay={40} distance={36} variant="left" className="order-2 lg:order-1">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="secondary" className="stagger-item" style={{ "--stagger-delay": "0ms" }}>
                  Local NL/DE
                </Badge>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Pilot product
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mt-3">
                {featuredProduct.name}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                A limited-run Roskam wool knit designed for everyday wear. Crafted in small batches with
                premium materials and careful finishing to showcase the natural character of the fiber.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-border/60 bg-background/80 p-4 stagger-item" style={{ "--stagger-delay": "80ms" }}>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Material</p>
                  <p className="mt-1 text-sm font-medium text-foreground">Roskam wool blend</p>
                </div>
                <div className="rounded-lg border border-border/60 bg-background/80 p-4 stagger-item" style={{ "--stagger-delay": "150ms" }}>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Origin</p>
                  <p className="mt-1 text-sm font-medium text-foreground">Roskam family farm</p>
                </div>
                <div className="rounded-lg border border-border/60 bg-background/80 p-4 stagger-item" style={{ "--stagger-delay": "220ms" }}>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Run</p>
                  <p className="mt-1 text-sm font-medium text-foreground">Small-batch pilot</p>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button asChild size="lg" className="btn-premium">
                  <a href="#waitlist">
                    Join the waitlist
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/about">About the farm</Link>
                </Button>
              </div>
            </Reveal>

            <Reveal delay={140} distance={34} variant="right" className="order-1 lg:order-2">
              <Card className="overflow-hidden border-border/50 bg-background/90 backdrop-blur">
                <div className="aspect-[4/5] overflow-hidden bg-secondary/30 relative">
                  <Carousel
                    className="h-full"
                    opts={{ loop: featuredProduct.productImages.length > 1 }}
                    setApi={setProductCarouselApi}
                  >
                    <CarouselContent className="ml-0 h-full">
                      {featuredProduct.productImages.map((image, index) => (
                        <CarouselItem key={image} className="pl-0 h-full">
                          <img
                            src={image}
                            alt={`${featuredProduct.name} view ${index + 1}`}
                            className="w-full h-full object-contain p-4 transition-transform ease-out hover:scale-[1.03]"
                            style={{ transitionDuration: "1.8s" }}
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>

                    {featuredProduct.productImages.length > 1 ? (
                      <>
                        <CarouselPrevious
                          className="left-3 top-1/2 h-9 w-9 -translate-y-1/2 border-border/70 bg-background/85 backdrop-blur hover:bg-background"
                        />
                        <CarouselNext
                          className="right-3 top-1/2 h-9 w-9 -translate-y-1/2 border-border/70 bg-background/85 backdrop-blur hover:bg-background"
                        />
                      </>
                    ) : null}
                  </Carousel>

                  {featuredProduct.productImages.length > 1 ? (
                    <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-2 backdrop-blur">
                      {featuredProduct.productImages.map((image, index) => (
                        <button
                          key={`${image}-indicator`}
                          type="button"
                          aria-label={`Go to product image ${index + 1}`}
                          onClick={() => productCarouselApi?.scrollTo(index)}
                          className={`h-2.5 w-2.5 rounded-full transition-colors ${
                            selectedProductImage === index ? "bg-foreground" : "bg-foreground/25"
                          }`}
                        />
                      ))}
                    </div>
                  ) : (
                    <span className="absolute bottom-4 right-4 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur">
                      Pilot image
                    </span>
                  )}
                </div>
                <CardContent className="p-6">
                  <TypedHandStyle
                    text={"\"Every Roskam pilot product is built to highlight the farm’s wool and the hands that craft it.\""}
                    start
                    startDelayMs={180}
                    speedMultiplier={1.2}
                    className="text-sm text-muted-foreground italic"
                  />
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <Reveal variant="left" distance={32}>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
                Product details
              </h2>
              <p className="mt-3 text-lg text-muted-foreground">
                Fit, care, and sizing information for the pilot release.
              </p>
              <ProductInformation sizing={productSizing} care={productCare} className="mt-6" />
              <Card className="mt-6 border-border/60 bg-secondary/10">
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Pilot note</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Full traceability documents will be shared with the waitlist before launch.
                  </p>
                </CardContent>
              </Card>
            </Reveal>

            <Reveal delay={140} variant="right" className="space-y-6">
              <TraceabilityPanel
                title="How it works"
                subtitle="A short view of the pilot product supply chain."
                materialComposition={
                  <>
                    Wool sourced via{" "}
                    <a
                      href="https://www.suedwollegroup.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-foreground underline underline-offset-4"
                    >
                      Sued Wolle Group
                    </a>{" "}
                    and produced with{" "}
                    <a
                      href="https://knitwearlab.co/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-foreground underline underline-offset-4"
                    >
                      Knitwear Lab
                    </a>
                    .
                  </>
                }
                traceability={traceability}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="py-20 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Get early access</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mt-3">
                Join the waitlist
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                We will share sizing confirmations, pricing, and drop timing directly with the waitlist.
              </p>
              <div className="mt-6 space-y-3 text-sm text-muted-foreground">
                <p className="flex items-center gap-2"><Circle className="h-2 w-2" /> First access to the pilot drop.</p>
                <p className="flex items-center gap-2"><Circle className="h-2 w-2" /> Updates on production milestones.</p>
                <p className="flex items-center gap-2"><Circle className="h-2 w-2" /> Traceability documents before launch.</p>
              </div>
            </div>

            <Card className="border-border/60 tilt-card">
              <CardContent className="p-8">
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleWaitlistSubmit}>
                  <div className="md:col-span-1">
                    <label className="text-sm font-medium text-foreground">Name</label>
                    <Input
                      value={waitlistForm.name}
                      onChange={(event) => setWaitlistForm({ ...waitlistForm, name: event.target.value })}
                      placeholder="Your name"
                      className="mt-2"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="text-sm font-medium text-foreground">Email</label>
                    <Input
                      type="email"
                      required
                      value={waitlistForm.email}
                      onChange={(event) => setWaitlistForm({ ...waitlistForm, email: event.target.value })}
                      placeholder="you@email.com"
                      className="mt-2"
                    />
                  </div>
                  <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-sm text-muted-foreground">
                      We will only use your email for waitlist updates.
                    </p>
                    <Button type="submit" size="lg" disabled={isWaitlistSubmitting}>
                      {isWaitlistSubmitting ? "Submitting..." : "Join waitlist"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <Card className="order-2 lg:order-1 border-border/60 tilt-card tilt-card-opposite">
              <CardContent className="p-8">
                <form className="grid grid-cols-1 gap-6" onSubmit={handleContactSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-foreground">Name</label>
                      <Input
                        value={contactForm.name}
                        onChange={(event) => setContactForm({ ...contactForm, name: event.target.value })}
                        placeholder="Your name"
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Email</label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })}
                        placeholder="you@email.com"
                        className="mt-2"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Message</label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(event) => setContactForm({ ...contactForm, message: event.target.value })}
                      placeholder="Tell us what you are looking for."
                      className="mt-2 min-h-[140px]"
                      required
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <p className="text-sm text-muted-foreground">
                      We reply within 24 hours.
                    </p>
                    <Button type="submit" size="lg" disabled={isContactSubmitting}>
                      {isContactSubmitting ? "Sending..." : "Send message"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <div className="order-1 lg:order-2">
              <Card className="border-border/60 bg-secondary/20">
              <CardContent className="p-8">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Contact</p>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mt-3">
                  Let’s talk
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Questions about the pilot product or the farm? Send a message.
                </p>
                <p className="mt-6 text-sm text-muted-foreground">
                  Messages are routed to our inbox and answered as quickly as possible.
                </p>
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaSectionRef} className="py-20 lg:py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <TypedHandStyle
            text={CTA_HEADLINE}
            start={startCtaTyping}
            startDelayMs={120}
            speedMultiplier={1.4}
            className="mx-auto text-primary-foreground font-display text-3xl sm:text-4xl font-semibold"
          />
          <p className="mt-4 text-lg opacity-90 max-w-2xl mx-auto italic">
            Join the waitlist to follow the pilot launch and reserve early access.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="btn-premium">
              <a href="#waitlist">
                Join the waitlist
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Policies */}
      <section id="policies" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal as="div" className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              Policies
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Standard return and privacy information for the pilot product.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Reveal variant="left" as="div">
              <Card id="returns" className="border-border/60">
                <CardContent className="p-8">
                  <h3 className="font-display text-2xl font-semibold text-foreground">Return Policy</h3>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                    We accept returns within 30 days of delivery for items that are unused, unwashed, and in their
                    original condition with all tags attached. To start a return, email mvonk4015@gmail.com with your
                    order number and the reason for the return. Once approved, items must be sent back within 14 days.
                    Refunds are issued to the original payment method within 10 business days after the return is
                    received and inspected. Shipping fees are non-refundable, and return shipping is the customer’s
                    responsibility unless the item is defective or incorrect. Because this is a pilot product, exchanges
                    may be limited to stock availability.
                  </p>
                </CardContent>
              </Card>
            </Reveal>

            <Reveal variant="right" delay={100} as="div">
              <Card id="privacy" className="border-border/60">
                <CardContent className="p-8">
                  <h3 className="font-display text-2xl font-semibold text-foreground">Privacy Policy</h3>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                    We collect only the information you provide through the waitlist and contact forms (name, email, and
                    message content). This data is used solely to respond to inquiries, manage the waitlist, and share
                    pilot product updates. We do not sell personal data. Data may be processed by service providers that
                    help us deliver email communications. As a Netherlands-based business, we follow GDPR principles and
                    keep data only as long as needed for the pilot project. You can request access, correction, or
                    deletion of your data at any time by emailing mvonk4015@gmail.com.
                  </p>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
